// Banners.jsx
import React, { useState, useEffect, useRef } from 'react';
import Sidebar from '../../components/sidebar';
import Header from '../../components/header';
import { apiCall } from '../../utils/api';
import toast, { Toaster } from 'react-hot-toast';
import { FaSearch, FaEdit, FaTrash, FaPlus, FaToggleOn, FaToggleOff, FaUpload, FaTimes, FaCrop } from "react-icons/fa";
import Swal from 'sweetalert2';
import ReactCrop from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';

function Banners() {
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [currentPage, setCurrentPage] = useState('dashboard');
    const [banners, setBanners] = useState([]);
    const [filteredBanners, setFilteredBanners] = useState([]);
    const [loading, setLoading] = useState(false);
    const [showFilters, setShowFilters] = useState(true);

    // Modal states
    const [bannerModalOpen, setBannerModalOpen] = useState(false);
    const [selectedBanner, setSelectedBanner] = useState(null);
    const [isEditMode, setIsEditMode] = useState(false);
    const [crop, setCrop] = useState({
    unit: '%',
    x: 5,
    y: 5,
    width: 90,
    height: 50
});

    // Pagination states
    const [pagination, setPagination] = useState({
        currentPage: 1,
        perPage: 10,
        totalRecords: 0,
        totalPages: 1
    });

    // Filter states
    const [filters, setFilters] = useState({
        searchValue: '',
        status: ''
    });

    // Status options
    const statusOptions = [
        { id: '', name: 'All Status' },
        { id: '1', name: 'Active' },
        { id: '0', name: 'Inactive' }
    ];

    // Crop states
    const [imageFile, setImageFile] = useState(null);
    const [imagePreview, setImagePreview] = useState('');

    const [completedCrop, setCompletedCrop] = useState(null);
    const [imageRef, setImageRef] = useState(null);
    const [isCropping, setIsCropping] = useState(false);
    const [showCropModal, setShowCropModal] = useState(false);
    const fileInputRef = useRef(null);
    const imageRefCallback = useRef(null);

    // Form state for add/edit
    const [formData, setFormData] = useState({
        image_url: '',
        redirect_url: '',
        status: '1'
    });

    // Fetch Banners with pagination
    const fetchBanners = async (page = 1) => {
        setLoading(true);
        try {
            const params = {
                searchValue: filters.searchValue,
                status: filters.status,
                page: page,
                limit: pagination.perPage
            };

            const result = await apiCall(
                "banners",
                params,
                true
            );

            if (result.status === true) {
                setBanners(result.data || []);
                setFilteredBanners(result.data || []);
                if (result.pagination) {
                    setPagination(result.pagination);
                }
            } else {
                toast.error(result.message || "Failed to fetch banners");
                setBanners([]);
                setFilteredBanners([]);
            }
        } catch (error) {
            toast.error("Network error. Please try again.");
            setBanners([]);
            setFilteredBanners([]);
        } finally {
            setLoading(false);
        }
    };

    // Handle Add Banner
    const handleAddBanner = () => {
        setIsEditMode(false);
        setFormData({
            image_url: '',
            redirect_url: '',
            status: '1'
        });
        setImagePreview('');
        setImageFile(null);
        setCompletedCrop(null);
        setSelectedBanner(null);
        setBannerModalOpen(true);
    };

    // Handle Edit Banner
    const handleEditBanner = (banner) => {
        setIsEditMode(true);
        setSelectedBanner(banner);
        setFormData({
            image_url: banner.image_url || '',
            redirect_url: banner.redirect_url || '',
            status: banner.status_id || '1'
        });
        setImagePreview(banner.image_url || '');
        setImageFile(null);
        setCompletedCrop(null);
        setBannerModalOpen(true);
    };

    // Handle Delete Banner
    const handleDeleteBanner = (banner) => {
        Swal.fire({
            title: 'Delete Banner?',
            text: `Are you sure you want to delete this banner?`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: 'Yes, Delete!',
            cancelButtonText: 'Cancel'
        }).then(async (result) => {
            if (result.isConfirmed) {
                try {
                    const response = await apiCall(
                        "delete-banner",
                        {
                            banner_id: banner.id
                        },
                        true
                    );

                    if (response.status === true) {
                        toast.success("Banner deleted successfully!");
                        fetchBanners(pagination.currentPage);
                    } else {
                        toast.error(response.message || "Failed to delete banner");
                    }
                } catch (error) {
                    toast.error("Network error. Please try again.");
                }
            }
        });
    };

    // Handle Status Toggle
    const handleStatusToggle = (banner) => {
        const newStatus = banner.status === 'Active' ? '0' : '1';
        const statusName = banner.status === 'Active' ? 'Inactive' : 'Active';

        Swal.fire({
            title: `Toggle Status?`,
            text: `Are you sure you want to change this banner to ${statusName}?`,
            icon: 'question',
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            confirmButtonText: 'Yes, Toggle!',
            cancelButtonText: 'Cancel'
        }).then(async (result) => {
            if (result.isConfirmed) {
                try {
                    const response = await apiCall(
                        "toggle-banner-status",
                        {
                            banner_id: banner.id,
                            status: newStatus
                        },
                        true
                    );

                    if (response.status === true) {
                        toast.success(`Banner ${statusName} successfully!`);
                        fetchBanners(pagination.currentPage);
                    } else {
                        toast.error(response.message || "Failed to toggle status");
                    }
                } catch (error) {
                    toast.error("Network error. Please try again.");
                }
            }
        });
    };

    // Handle Image Upload
    const handleImageUpload = (e) => {
        const file = e.target.files[0];
        if (file) {
            if (!file.type.startsWith('image/')) {
                toast.error('Please upload an image file');
                return;
            }

            if (file.size > 5 * 1024 * 1024) {
                toast.error('Image size should be less than 5MB');
                return;
            }

            const reader = new FileReader();
            reader.onload = () => {
                setImagePreview(reader.result);
                setImageFile(file);
                // Open crop modal after image loads
                setTimeout(() => {
                    setShowCropModal(true);
                }, 100);
            };
            reader.readAsDataURL(file);
        }
    };

    // Handle Crop Complete
    const handleCropComplete = (crop) => {
        setCompletedCrop(crop);
    };

    // Handle Crop and Upload
    const handleCropAndUpload = async () => {
        // Use the imageRef from the callback
        const img = imageRefCallback.current;
        
        if (!img || !completedCrop) {
            toast.error("Please select a crop area");
            return;
        }

        setIsCropping(true);

        try {
            const canvas = document.createElement('canvas');
            const image = img;
            
            // Get the crop dimensions in pixels
            const scaleX = image.naturalWidth / image.width;
            const scaleY = image.naturalHeight / image.height;
            
            const cropWidth = completedCrop.width * scaleX;
            const cropHeight = completedCrop.height * scaleY;
            const cropX = completedCrop.x * scaleX;
            const cropY = completedCrop.y * scaleY;

            // Set canvas to 16:9 ratio
            const aspectRatio = 16 / 9;
            let canvasWidth, canvasHeight;

            if (cropWidth / cropHeight > aspectRatio) {
                canvasHeight = Math.round(cropHeight);
                canvasWidth = Math.round(cropHeight * aspectRatio);
            } else {
                canvasWidth = Math.round(cropWidth);
                canvasHeight = Math.round(cropWidth / aspectRatio);
            }

            canvas.width = canvasWidth;
            canvas.height = canvasHeight;

            const ctx = canvas.getContext('2d');
            
            // Draw the cropped portion centered
            const centerX = cropX + (cropWidth - canvasWidth) / 2;
            const centerY = cropY + (cropHeight - canvasHeight) / 2;
            
            ctx.drawImage(
                image,
                Math.round(centerX),
                Math.round(centerY),
                canvasWidth,
                canvasHeight,
                0,
                0,
                canvasWidth,
                canvasHeight
            );

            // Convert to base64
            const croppedImage = canvas.toDataURL('image/jpeg', 0.95);
            
            setFormData({ ...formData, image_url: croppedImage });
            setImagePreview(croppedImage);
            setImageFile(null);
            setIsCropping(false);
            setShowCropModal(false);
            setCompletedCrop(null);
            toast.success("Image cropped successfully!");

        } catch (error) {
            console.error('Crop error:', error);
            toast.error("Failed to crop image");
            setIsCropping(false);
        }
    };

    // Cancel Crop
    const cancelCrop = () => {
        setShowCropModal(false);
        setCompletedCrop(null);
        setIsCropping(false);
        if (!formData.image_url) {
            setImagePreview('');
        }
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    // Remove image
    const removeImage = () => {
        setImagePreview('');
        setImageFile(null);
        setFormData({ ...formData, image_url: '' });
        setCompletedCrop(null);
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    // Handle Save Banner
    const handleSaveBanner = async () => {
        const imageToSave = formData.image_url || imagePreview;

        if (!imageToSave) {
            toast.error("Please upload and crop an image");
            return;
        }

        if (!formData.redirect_url.trim()) {
            toast.error("Please enter redirect URL");
            return;
        }

        try {
            const endpoint = isEditMode ? "update-banner" : "add-banner";
            const params = isEditMode
                ? { banner_id: selectedBanner.id, image_url: imageToSave, redirect_url: formData.redirect_url, status: formData.status }
                : { image_url: imageToSave, redirect_url: formData.redirect_url, status: formData.status };

            const response = await apiCall(endpoint, params, true);

            if (response.status === true) {
                toast.success(isEditMode ? "Banner updated successfully!" : "Banner added successfully!");
                setBannerModalOpen(false);
                fetchBanners(pagination.currentPage);
            } else {
                toast.error(response.message || "Failed to save banner");
            }
        } catch (error) {
            toast.error("Network error. Please try again.");
        }
    };

    // Initial fetch
    useEffect(() => {
        fetchBanners(1);
    }, []);

    // Apply filters
    const applyFilters = () => {
        fetchBanners(1);
    };

    // Reset filters
    const resetFilters = () => {
        setFilters({
            searchValue: '',
            status: ''
        });
        fetchBanners(1);
    };

    // Handle page change
    const handlePageChange = (newPage) => {
        if (newPage >= 1 && newPage <= pagination.totalPages) {
            fetchBanners(newPage);
        }
    };

    // Handle filter change
    const handleFilterChange = (key, value) => {
        setFilters(prev => ({ ...prev, [key]: value }));
    };

    // Get status color
    const getStatusColor = (status) => {
        const colors = {
            'Active': 'bg-green-100 text-green-800',
            'Inactive': 'bg-red-100 text-red-800'
        };
        return colors[status] || 'bg-gray-100 text-gray-800';
    };

    return (
        <div className="bg-gray-50 font-sans antialiased min-h-screen">
            {/* Mobile Overlay */}
            {sidebarOpen && (
                <div
                    className="fixed inset-0 bg-black/50 z-40 lg:hidden"
                    onClick={() => setSidebarOpen(false)}
                />
            )}

            <Sidebar
                currentPage={currentPage}
                setCurrentPage={setCurrentPage}
                isOpen={sidebarOpen}
                setIsOpen={setSidebarOpen}
            />

            <div className="main-content lg:ml-[72px] transition-all duration-200">
                <Header
                    currentPage={currentPage}
                    sidebarOpen={sidebarOpen}
                    setSidebarOpen={setSidebarOpen}
                />

                <div className="p-4 sm:p-6 lg:p-8">
                    {/* Page Title */}
                    <div className="mb-6 flex justify-between items-center flex-wrap gap-2">
                        <div>
                            <h1 className="text-2xl font-bold text-gray-900">Banners</h1>
                            <p className="text-gray-600 text-sm">View and manage all banners</p>
                        </div>
                        <button
                            onClick={handleAddBanner}
                            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition-colors duration-200 text-sm flex items-center gap-2"
                        >
                            <FaPlus />
                            Add Banner
                        </button>
                    </div>

                    {/* Filter Section */}
                    <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-6 overflow-hidden">
                        <div className={`p-4 sm:p-6 ${showFilters ? 'block' : 'hidden lg:block'}`}>
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                                {/* Search */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Search</label>
                                    <div className="relative">
                                        <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                                        <input
                                            type="text"
                                            value={filters.searchValue}
                                            onChange={(e) => handleFilterChange('searchValue', e.target.value)}
                                            placeholder="Search by URL..."
                                            className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                                        />
                                    </div>
                                </div>

                                {/* Status */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                                    <select
                                        value={filters.status}
                                        onChange={(e) => handleFilterChange('status', e.target.value)}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                                    >
                                        {statusOptions.map(option => (
                                            <option key={option.id} value={option.id}>{option.name}</option>
                                        ))}
                                    </select>
                                </div>

                                {/* Actions */}
                                <div className="flex gap-2 items-end">
                                    <button onClick={applyFilters} className="flex-1 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition-colors duration-200 text-sm">Apply</button>
                                    <button onClick={resetFilters} className="flex-1 px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium rounded-lg transition-colors duration-200 text-sm">Reset</button>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Results Count */}
                    <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 mb-4">
                        <p className="text-sm text-gray-600">
                            Showing <span className="font-semibold text-gray-900">{filteredBanners.length}</span> banners
                            {pagination.totalRecords > 0 && ` of ${pagination.totalRecords}`}
                        </p>
                        {loading && (
                            <div className="flex items-center text-sm text-gray-500">
                                <svg className="animate-spin h-4 w-4 mr-2 text-blue-600" fill="none" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                                </svg>
                                Loading...
                            </div>
                        )}
                    </div>

                    {/* Banners Table */}
                    <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                        <div className="overflow-x-auto">
                            <table className="min-w-full divide-y divide-gray-200">
                                <thead className="bg-gray-50">
                                    <tr>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">ID</th>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Image</th>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Redirect URL</th>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                    {filteredBanners.length > 0 ? (
                                        filteredBanners.map((banner) => (
                                            <tr key={banner.id} className="hover:bg-gray-50 transition-colors duration-150">
                                                <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-blue-600">#{banner.id}</td>
                                                <td className="px-4 py-3 whitespace-nowrap">
                                                    <img 
                                                        src={banner.image_url} 
                                                        alt="Banner" 
                                                        className="w-20 h-12 object-cover rounded-lg border border-gray-200"
                                                        onError={(e) => { e.target.src = 'https://via.placeholder.com/100x56?text=No+Image'; }}
                                                    />
                                                </td>
                                                <td className="px-4 py-3 text-sm text-gray-600 max-w-xs truncate">
                                                    <a href={banner.redirect_url} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                                                        {banner.redirect_url}
                                                    </a>
                                                </td>
                                                <td className="px-4 py-3 whitespace-nowrap">
                                                    <div className="flex items-center gap-2">
                                                        <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusColor(banner.status)}`}>
                                                            {banner.status}
                                                        </span>
                                                        <button onClick={() => handleStatusToggle(banner)} className="text-gray-500 hover:text-blue-600 transition-colors">
                                                            {banner.status === 'Active' ? <FaToggleOn className="text-2xl text-green-600" /> : <FaToggleOff className="text-2xl text-gray-400" />}
                                                        </button>
                                                    </div>
                                                </td>
                                                <td className="px-4 py-3">
                                                    <div className="flex items-center gap-2">
                                                        <button onClick={() => handleEditBanner(banner)} className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"><FaEdit className="text-sm" /></button>
                                                        <button onClick={() => handleDeleteBanner(banner)} className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"><FaTrash className="text-sm" /></button>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))
                                    ) : (
                                        <tr>
                                            <td colSpan="5" className="px-4 py-8 text-center text-gray-500">
                                                <div className="flex flex-col items-center">
                                                    <svg className="w-12 h-12 text-gray-300 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
                                                    </svg>
                                                    <p className="text-sm">No banners found</p>
                                                    <p className="text-xs text-gray-400 mt-1">Try adjusting your filters</p>
                                                </div>
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>

                    {/* Pagination */}
                    {pagination.totalPages > 1 && (
                        <div className="flex flex-col sm:flex-row justify-between items-center gap-4 mt-6">
                            <div className="text-sm text-gray-600">Page {pagination.currentPage} of {pagination.totalPages}</div>
                            <div className="flex gap-2">
                                <button onClick={() => handlePageChange(pagination.currentPage - 1)} disabled={pagination.currentPage === 1} className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors duration-200 ${pagination.currentPage === 1 ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-blue-600 text-white hover:bg-blue-700'}`}>Previous</button>
                                <div className="flex gap-1">
                                    {Array.from({ length: Math.min(5, pagination.totalPages) }, (_, i) => {
                                        let pageNum;
                                        if (pagination.totalPages <= 5) pageNum = i + 1;
                                        else if (pagination.currentPage <= 3) pageNum = i + 1;
                                        else if (pagination.currentPage >= pagination.totalPages - 2) pageNum = pagination.totalPages - 4 + i;
                                        else pageNum = pagination.currentPage - 2 + i;
                                        return (
                                            <button key={pageNum} onClick={() => handlePageChange(pageNum)} className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors duration-200 ${pagination.currentPage === pageNum ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}>{pageNum}</button>
                                        );
                                    })}
                                </div>
                                <button onClick={() => handlePageChange(pagination.currentPage + 1)} disabled={pagination.currentPage === pagination.totalPages} className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors duration-200 ${pagination.currentPage === pagination.totalPages ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-blue-600 text-white hover:bg-blue-700'}`}>Next</button>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Add/Edit Banner Modal */}
            {bannerModalOpen && (
                <div className="fixed inset-0 z-50 overflow-y-auto">
                    <div className="fixed inset-0 bg-black/50 transition-opacity" onClick={() => setBannerModalOpen(false)}></div>
                    <div className="flex min-h-full items-center justify-center p-4">
                        <div className="relative bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col">
                            <div className="flex-shrink-0 bg-white border-b border-gray-200 px-6 py-4 flex justify-between items-center">
                                <div>
                                    <h3 className="text-xl font-semibold text-gray-900">{isEditMode ? 'Edit Banner' : 'Add New Banner'}</h3>
                                    <p className="text-sm text-gray-500">{isEditMode ? `Editing banner #${selectedBanner?.id}` : 'Create a new banner'}</p>
                                </div>
                                <button onClick={() => setBannerModalOpen(false)} className="text-gray-400 hover:text-gray-600 transition-colors p-2 hover:bg-gray-100 rounded-lg">
                                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" /></svg>
                                </button>
                            </div>

                            <div className="flex-1 overflow-y-auto p-6">
                                <div className="space-y-4">
                                    {/* Image Upload */}
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Banner Image <span className="text-red-500">*</span></label>
                                        <div className="mt-1 flex flex-col items-center justify-center border-2 border-dashed border-gray-300 rounded-lg p-6 hover:bg-gray-50 transition-colors">
                                            {imagePreview ? (
                                                <div className="relative w-full">
                                                    <img 
                                                        src={imagePreview} 
                                                        alt="Banner preview" 
                                                        className="w-full h-auto max-h-48 object-contain rounded-lg"
                                                    />
                                                    <div className="absolute top-2 right-2 flex gap-2">
                                                        {!formData.image_url && imageFile && (
                                                            <button
                                                                onClick={() => setShowCropModal(true)}
                                                                className="p-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                                                                title="Crop Image"
                                                            >
                                                                <FaCrop className="text-sm" />
                                                            </button>
                                                        )}
                                                        <button
                                                            onClick={removeImage}
                                                            className="p-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                                                            title="Remove Image"
                                                        >
                                                            <FaTimes className="text-sm" />
                                                        </button>
                                                    </div>
                                                    <p className="text-xs text-gray-500 mt-2 text-center">Click Crop to adjust 16:9 ratio</p>
                                                </div>
                                            ) : (
                                                <div className="text-center">
                                                    <FaUpload className="mx-auto h-12 w-12 text-gray-400" />
                                                    <div className="mt-4 flex text-sm leading-6 text-gray-600">
                                                        <label className="relative cursor-pointer rounded-md bg-white font-semibold text-blue-600 focus-within:outline-none focus-within:ring-2 focus-within:ring-blue-600 focus-within:ring-offset-2 hover:text-blue-500">
                                                            <span>Upload an image</span>
                                                            <input
                                                                ref={fileInputRef}
                                                                type="file"
                                                                className="sr-only"
                                                                accept="image/*"
                                                                onChange={handleImageUpload}
                                                            />
                                                        </label>
                                                        <p className="pl-1">or drag and drop</p>
                                                    </div>
                                                    <p className="text-xs leading-5 text-gray-500">PNG, JPG, GIF up to 5MB</p>
                                                    <p className="text-xs leading-5 text-gray-400">Recommended ratio: 16:9 (landscape)</p>
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    {/* Redirect URL */}
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Redirect URL <span className="text-red-500">*</span></label>
                                        <input
                                            type="text"
                                            value={formData.redirect_url}
                                            onChange={(e) => setFormData({ ...formData, redirect_url: e.target.value })}
                                            placeholder="Enter redirect URL..."
                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                                        />
                                    </div>

                                    {/* Status */}
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                                        <select
                                            value={formData.status}
                                            onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                                        >
                                            <option value="1">Active</option>
                                            <option value="0">Inactive</option>
                                        </select>
                                    </div>
                                </div>
                            </div>

                            <div className="flex-shrink-0 bg-white border-t border-gray-200 px-6 py-3 flex justify-end gap-2">
                                <button onClick={() => setBannerModalOpen(false)} className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium rounded-lg transition-colors duration-200 text-sm">Cancel</button>
                                <button onClick={handleSaveBanner} className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition-colors duration-200 text-sm">{isEditMode ? 'Update' : 'Add'} Banner</button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Crop Modal */}
            {showCropModal && imagePreview && (
                <div className="fixed inset-0 z-50 overflow-y-auto">
                    <div className="fixed inset-0 bg-black/75 transition-opacity" onClick={cancelCrop}></div>
                    <div className="flex min-h-full items-center justify-center p-4">
                        <div className="relative bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
                            <div className="flex-shrink-0 bg-white border-b border-gray-200 px-6 py-4 flex justify-between items-center">
                                <div>
                                    <h3 className="text-xl font-semibold text-gray-900">Crop Image</h3>
                                    <p className="text-sm text-gray-500">Crop to 16:9 (landscape) ratio</p>
                                </div>
                                <button onClick={cancelCrop} className="text-gray-400 hover:text-gray-600 transition-colors p-2 hover:bg-gray-100 rounded-lg">
                                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" /></svg>
                                </button>
                            </div>

                            <div className="flex-1 overflow-y-auto p-6">
                                <div className="max-h-[60vh] overflow-auto flex justify-center">
                                    <ReactCrop
    crop={crop}
    onChange={(c) => setCrop(c)}
    onComplete={(c) => setCompletedCrop(c)}
    aspect={16 / 9}
>
    <img
        src={imagePreview}
        alt="Crop"
        onLoad={(e) => {
            imageRefCallback.current = e.currentTarget;
        }}
        style={{
            maxWidth: "100%",
            maxHeight: "60vh",
            objectFit: "contain"
        }}
    />
</ReactCrop>
                                </div>
                                <div className="mt-4 flex justify-center gap-2">
                                    <button onClick={cancelCrop} className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium rounded-lg transition-colors duration-200 text-sm">Cancel</button>
                                    <button 
                                        onClick={handleCropAndUpload} 
                                        disabled={isCropping}
                                        className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition-colors duration-200 text-sm disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                                    >
                                        {isCropping ? 'Cropping...' : 'Apply Crop'}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            <Toaster position="top-right" />
        </div>
    );
}

export default Banners;